import React, { useMemo, useState } from 'react';
import {
  hesaplaVekaletUcreti,
  hesaplaIcraVekaletUcreti,
  parseTutar,
  formatTL,
  AAUT_NISPI_DILIMLER,
  AAUT_TARIFE_ADI,
  AAUT_RESMI_GAZETE,
  AAUT_MAHKEME_MAKTU,
  AAUT_ICRA_MAKTU,
  AAUT_ICRA_NISPI_ESIK,
} from '../utils/vekaletUcreti';
import './VekaletUcretiHesapla.css';

const VekaletUcretiHesapla = () => {
  /** 'icra' | 'dava' — iki ayrı kural seti (AAÜT m.11 ve m.13). */
  const [tur, setTur] = useState('dava');
  const [deger, setDeger] = useState('');
  const [mahkeme, setMahkeme] = useState('asliye');
  const [sureIcindeOdendi, setSureIcindeOdendi] = useState(false);
  const [tarifeGoster, setTarifeGoster] = useState(false);

  const seciliMahkeme = AAUT_MAHKEME_MAKTU.find((m) => m.anahtar === mahkeme)
    ?? AAUT_MAHKEME_MAKTU[0];

  const sonuc = useMemo(
    () => (tur === 'icra'
      ? hesaplaIcraVekaletUcreti(parseTutar(deger), sureIcindeOdendi)
      : hesaplaVekaletUcreti(parseTutar(deger), seciliMahkeme.maktu)),
    [tur, deger, seciliMahkeme, sureIcindeOdendi]
  );

  const girildi = parseTutar(deger) > 0;

  return (
    <div className="vu-page">
      <div className="page-header vu-header">
        <div>
          <h1>Vekalet Ücreti Hesapla</h1>
          <p className="vu-header-sub">
            {AAUT_TARIFE_ADI} · Nispi (oransal) tarife — Üçüncü Kısım
          </p>
        </div>
      </div>

      <div className="vu-grid">
        {/* ---------- Girdi ---------- */}
        <div className="vu-card">
          <h2 className="vu-card-title">Bilgiler</h2>

          {/* İcra ve dava için tarifede AYRI kurallar var; önce bunu seçin. */}
          <div className="vu-tur">
            {[
              ['dava', 'Konusu Para Olan Dava'],
              ['icra', 'İcra Takibi'],
            ].map(([k, ad]) => (
              <button
                key={k}
                type="button"
                className={'vu-tur-dugme' + (tur === k ? ' aktif' : '')}
                onClick={() => setTur(k)}
              >
                {ad}
              </button>
            ))}
          </div>

          <div className="form-group">
            <label htmlFor="vu-deger">
              {tur === 'icra' ? 'Takip Miktarı (TL)' : 'Kabul / Reddedilen Miktar (TL)'}
            </label>
            <input
              id="vu-deger"
              className="form-control"
              inputMode="decimal"
              placeholder="Örn. 1.250.000"
              value={deger}
              onChange={(e) => setDeger(e.target.value)}
              autoFocus
            />
            <small className="vu-hint">
              {tur === 'icra'
                ? 'Takip talebindeki asıl alacak. ' + formatTL(AAUT_ICRA_NISPI_ESIK)
                  + ' TL\'ye kadar maktu ücret uygulanır (AAÜT m.11/1).'
                : 'Dava değeri ya da hükmedilen miktar. Nispi ücret bu tutar üzerinden hesaplanır.'}
            </small>
          </div>

          {tur === 'dava' ? (
            <div className="form-group">
              <label htmlFor="vu-mahkeme">Mahkeme</label>
              <select
                id="vu-mahkeme"
                className="form-control"
                value={mahkeme}
                onChange={(e) => setMahkeme(e.target.value)}
              >
                {AAUT_MAHKEME_MAKTU.map((m) => (
                  <option key={m.anahtar} value={m.anahtar}>
                    {m.ad} — maktu {formatTL(m.maktu)} TL
                  </option>
                ))}
              </select>
              <small className="vu-hint">
                AAÜT m.13/1: nispi ücret, davanın görüldüğü mahkeme için tarifede gösterilen
                maktu ücretin altında olamaz. Maktu tutarlar tarifeden alınmıştır, elle
                girmenize gerek yok.
              </small>
            </div>
          ) : (
            <div className="form-group">
              <label className="vu-onay">
                <input
                  type="checkbox"
                  checked={sureIcindeOdendi}
                  onChange={(e) => setSureIcindeOdendi(e.target.checked)}
                />
                <span>
                  <b>Borçlu ödeme süresi içinde ödedi</b>
                  <small>AAÜT m.11/4: bu durumda ücretin dörtte üçü takdir edilir.</small>
                </span>
              </label>
              <small className="vu-hint">
                İcra dairelerindeki takipler için maktu ücret {formatTL(AAUT_ICRA_MAKTU)} TL'dir
                ve <b>asıl alacağı geçemez</b> (AAÜT m.11/1 son cümle).
              </small>
            </div>
          )}

          <button
            type="button"
            className="btn vu-tarife-btn"
            onClick={() => setTarifeGoster((v) => !v)}
          >
            {tarifeGoster ? 'Tarife dilimlerini gizle' : 'Tarife dilimlerini göster'}
          </button>

          {tarifeGoster && (
            <table className="vu-tarife-tablo">
              <thead>
                <tr><th>Dilim</th><th>Oran</th></tr>
              </thead>
              <tbody>
                {AAUT_NISPI_DILIMLER.map((d) => (
                  <tr key={d.etiket}>
                    <td>{d.etiket}</td>
                    <td className="vu-num">%{d.oran}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ---------- Sonuç ---------- */}
        <div className="vu-card">
          <h2 className="vu-card-title">Sonuç</h2>

          {!girildi ? (
            <p className="vu-bos">Hesaplamak için bir miktar girin.</p>
          ) : (
            <>
              <div className="vu-sonuc">
                <span className="vu-sonuc-label">Vekalet Ücreti</span>
                <span className="vu-sonuc-tutar">{formatTL(sonuc.sonuc)} TL</span>
              </div>

              {sonuc.notlar.map((n, i) => (
                <div key={i} className="vu-not">{n.metin}</div>
              ))}

              <table className="vu-kirilim">
                <thead>
                  <tr>
                    <th>Dilim</th>
                    <th className="vu-num">Matrah</th>
                    <th className="vu-num">Oran</th>
                    <th className="vu-num">Ücret</th>
                  </tr>
                </thead>
                <tbody>
                  {sonuc.kirilim.map((k, i) => (
                    <tr key={i}>
                      <td>{k.etiket}</td>
                      <td className="vu-num">{formatTL(k.matrah)}</td>
                      <td className="vu-num">%{k.oran}</td>
                      <td className="vu-num">{formatTL(k.ucret)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Nispi ücret toplamı</td>
                    <td className="vu-num">{formatTL(sonuc.nispiUcret)}</td>
                  </tr>
                  {sonuc.maktu > 0 && (
                    <tr>
                      <td colSpan={3}>Maktu ücret (alt sınır)</td>
                      <td className="vu-num">{formatTL(sonuc.maktu)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </>
          )}
        </div>
      </div>

      <p className="vu-kaynak">
        Kaynak: {AAUT_TARIFE_ADI} — {AAUT_RESMI_GAZETE}. Sonucu resmî tarife metniyle
        karşılaştırmadan işleme esas almayın.
      </p>
    </div>
  );
};

export default VekaletUcretiHesapla;
